import React, { useState } from 'react';
import { Plus, Trash2, Edit2, MapPin, Users, Calendar, Search } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  ubicacion: string;
  telefono: string;
}
interface RutaCliente {
  clienteId: string;
  clienteNombre: string;
  codigo: string;
  ubicacion: string;
  estado: 'pendiente' | 'visitado' | 'sin_compra';
}
interface Ruta {
  id: string;
  nombre: string;
  zona: string;
  supervisor: string;
  clientes: RutaCliente[];
  fechaCreacion: string;
  diasSemana: string[];
  estado: 'activa' | 'inactiva';
}
interface RutasProps {
  rutas: Ruta[];
  clientes: Cliente[];
  onAddRuta: (ruta: Ruta) => void;
  onDeleteRuta: (id: string) => void;
  onUpdateRuta: (ruta: Ruta) => void;
}
const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
export function Rutas({
  rutas,
  clientes,
  onAddRuta,
  onDeleteRuta,
  onUpdateRuta
}: RutasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todas' | 'activa' | 'inactiva'>('activa');
  const [formData, setFormData] = useState<Partial<Ruta>>({
    estado: 'activa',
    clientes: [],
    diasSemana: []
  });
  const [selectedClientes, setSelectedClientes] = useState<string[]>([]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.zona || !formData.supervisor) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const rutaClientes: RutaCliente[] = selectedClientes.map(clienteId => {
      const cliente = clientes.find(c => c.id === clienteId);
      return {
        clienteId,
        clienteNombre: cliente?.nombre || '',
        codigo: cliente?.codigo || '',
        ubicacion: cliente?.ubicacion || '',
        estado: 'pendiente'
      };
    });
    if (editingId) {
      onUpdateRuta({
        ...formData,
        id: editingId,
        clientes: rutaClientes
      } as Ruta);
      setEditingId(null);
    } else {
      onAddRuta({
        ...formData,
        id: Date.now().toString(),
        clientes: rutaClientes,
        fechaCreacion: new Date().toISOString().split('T')[0]
      } as Ruta);
    }
    setFormData({
      estado: 'activa',
      clientes: [],
      diasSemana: []
    });
    setSelectedClientes([]);
    setShowForm(false);
  };
  const handleEdit = (ruta: Ruta) => {
    setFormData(ruta);
    setSelectedClientes(ruta.clientes.map(c => c.clienteId));
    setEditingId(ruta.id);
    setShowForm(true);
  };
  const toggleCliente = (clienteId: string) => {
    setSelectedClientes(prev => prev.includes(clienteId) ? prev.filter(id => id !== clienteId) : [...prev, clienteId]);
  };
  const toggleDia = (dia: string) => {
    setFormData(prev => ({
      ...prev,
      diasSemana: prev.diasSemana?.includes(dia) ? prev.diasSemana.filter(d => d !== dia) : [...(prev.diasSemana || []), dia]
    }));
  };
  const filteredRutas = rutas.filter(r => {
    const matchSearch = r.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || r.zona.toLowerCase().includes(searchTerm.toLowerCase()) || r.supervisor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todas' || r.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const rutasActivas = rutas.filter(r => r.estado === 'activa').length;
  return <div className="space-y-6" data-id="element-769">
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-770">
        <div style={glass} data-id="element-771">
          <div className="text-sm text-gray-400" data-id="element-772">Total Rutas</div>
          <div className="text-3xl font-bold text-blue-400" data-id="element-773">{rutas.length}</div>
        </div>
        <div style={glass} data-id="element-774">
          <div className="text-sm text-gray-400" data-id="element-775">Activas</div>
          <div className="text-3xl font-bold text-green-400" data-id="element-776">
            {rutasActivas}
          </div>
        </div>
        <div style={glass} data-id="element-777">
          <div className="text-sm text-gray-400" data-id="element-778">Total Clientes</div>
          <div className="text-3xl font-bold text-yellow-400" data-id="element-779">
            {rutas.reduce((sum, r) => sum + r.clientes.length, 0)}
          </div>
        </div>
      </div>

      {/* Controles */}
      <div style={glass} data-id="element-780">
        <div className="flex gap-4 flex-wrap" data-id="element-781">
          <div className="flex-1 min-w-[250px] relative" data-id="element-782">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" data-id="element-783" />
            <input type="text" placeholder="Buscar ruta, zona o supervisor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 38
          }} data-id="element-784" />
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-785">
            <option value="todas" data-id="element-786">Todas</option>
            <option value="activa" data-id="element-787">Activas</option>
            <option value="inactiva" data-id="element-788">Inactivas</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({});
          setSelectedClientes([]);
        }} style={primaryButton} data-id="element-789">
            <Plus className="w-4 h-4" data-id="element-790" /> Nueva Ruta
          </button>
        </div>
      </div>

      {/* Formulario */}
      {showForm && <div style={glass} data-id="element-791">
          <h3 className="text-lg font-bold mb-4" data-id="element-792">
            {editingId ? 'Editar Ruta' : 'Nueva Ruta'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-793">
            <div className="grid grid-cols-2 gap-4" data-id="element-794">
              <div data-id="element-795">
                <label className="text-sm text-gray-300" data-id="element-796">
                  Nombre de Ruta *
                </label>
                <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
              ...formData,
              nombre: e.target.value
            })} style={inputStyle} placeholder="Ruta Centro" data-id="element-797" />
              </div>
              <div data-id="element-798">
                <label className="text-sm text-gray-300" data-id="element-799">Zona *</label>
                <input type="text" value={formData.zona || ''} onChange={e => setFormData({
              ...formData,
              zona: e.target.value
            })} style={inputStyle} placeholder="Centro / Este / Norte" data-id="element-800" />
              </div>
              <div data-id="element-801">
                <label className="text-sm text-gray-300" data-id="element-802">Supervisor *</label>
                <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
              ...formData,
              supervisor: e.target.value
            })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-803" />
              </div>
              <div data-id="element-804">
                <label className="text-sm text-gray-300" data-id="element-805">Estado</label>
                <select value={formData.estado || 'activa'} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as any
            })} style={inputStyle} data-id="element-806">
                  <option value="activa" data-id="element-807">Activa</option>
                  <option value="inactiva" data-id="element-808">Inactiva</option>
                </select>
              </div>
            </div>

            <div data-id="element-809">
              <label className="text-sm text-gray-300 mb-2 block" data-id="element-810">
                Días de Operación
              </label>
              <div className="grid grid-cols-3 gap-2" data-id="element-811">
                {DIAS_SEMANA.map(dia => <label key={dia} className="flex items-center gap-2 cursor-pointer" data-id="element-812">
                    <input type="checkbox" checked={formData.diasSemana?.includes(dia) || false} onChange={() => toggleDia(dia)} className="w-4 h-4" data-id="element-813" />

                    <span className="text-sm" data-id="element-814">{dia}</span>
                  </label>)}
              </div>
            </div>

            <div data-id="element-815">
              <label className="text-sm text-gray-300 mb-2 block" data-id="element-816">
                Clientes en esta Ruta
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto bg-gray-900 p-3 rounded" data-id="element-817">
                {clientes.length === 0 ? <p className="text-gray-400 text-sm col-span-2" data-id="element-818">
                    No hay clientes disponibles
                  </p> : clientes.map(cliente => <label key={cliente.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded" data-id="element-819">
                      <input type="checkbox" checked={selectedClientes.includes(cliente.id)} onChange={() => toggleCliente(cliente.id)} className="w-4 h-4" data-id="element-820" />

                      <span className="text-sm" data-id="element-821">{cliente.nombre}</span>
                    </label>)}
              </div>
            </div>

            <div className="flex gap-2" data-id="element-822">
              <button type="submit" style={primaryButton} data-id="element-823">
                {editingId ? 'Actualizar' : 'Crear'} Ruta
              </button>
              <button type="button" onClick={() => {
            setShowForm(false);
            setEditingId(null);
            setSelectedClientes([]);
          }} style={ghostButton} data-id="element-824">
                Cancelar
              </button>
            </div>
          </form>
        </div>}

      {/* Lista de Rutas */}
      <div className="space-y-4" data-id="element-825">
        {filteredRutas.length === 0 ? <div className={glass + ' text-center py-8 text-gray-400'} data-id="element-826">
            No hay rutas disponibles
          </div> : filteredRutas.map(ruta => <div key={ruta.id} style={glass} data-id="element-827">
              <div className="flex items-start justify-between mb-4" data-id="element-828">
                <div data-id="element-829">
                  <h3 className="text-lg font-bold" data-id="element-830">{ruta.nombre}</h3>
                  <div className="flex gap-4 text-sm text-gray-400 mt-2" data-id="element-831">
                    <div className="flex items-center gap-1" data-id="element-832">
                      <MapPin className="w-4 h-4" data-id="element-833" />
                      {ruta.zona}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-834">
                      <Users className="w-4 h-4" data-id="element-835" />
                      Supervisor: {ruta.supervisor}
                    </div>
                    <div className="flex items-center gap-1" data-id="element-836">
                      <Calendar className="w-4 h-4" data-id="element-837" />
                      {ruta.diasSemana?.join(', ') || 'Sin días asignados'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-838">
                  <span className={`text-xs px-3 py-1 rounded ${ruta.estado === 'activa' ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`} data-id="element-839">
                    {ruta.estado}
                  </span>
                  <button onClick={() => handleEdit(ruta)} className="text-blue-400 hover:text-blue-300" data-id="element-840">
                    <Edit2 className="w-4 h-4" data-id="element-841" />
                  </button>
                  <button onClick={() => onDeleteRuta(ruta.id)} className="text-red-400 hover:text-red-300" data-id="element-842">
                    <Trash2 className="w-4 h-4" data-id="element-843" />
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4" data-id="element-844">
                <h4 className="text-sm font-semibold text-gray-300 mb-2" data-id="element-845">
                  Clientes Asignados ({ruta.clientes.length})
                </h4>
                {ruta.clientes.length === 0 ? <p className="text-sm text-gray-400" data-id="element-846">
                    Sin clientes asignados
                  </p> : <div className="overflow-x-auto" data-id="element-847">
                    <table className="w-full text-xs" data-id="element-848">
                      <thead data-id="element-849">
                        <tr className="border-b border-gray-700" data-id="element-850">
                          <th className="text-left py-1 px-2" data-id="element-851">Código</th>
                          <th className="text-left py-1 px-2" data-id="element-852">Nombre</th>
                          <th className="text-left py-1 px-2" data-id="element-853">Ubicación</th>
                          <th className="text-left py-1 px-2" data-id="element-854">Estado Visita</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-855">
                        {ruta.clientes.map((cliente, idx) => <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/30" data-id="element-856">
                            <td className="py-1 px-2 font-mono text-blue-400" data-id="element-857">
                              {cliente.codigo}
                            </td>
                            <td className="py-1 px-2" data-id="element-858">
                              {cliente.clienteNombre}
                            </td>
                            <td className="py-1 px-2" data-id="element-859">{cliente.ubicacion}</td>
                            <td className="py-1 px-2" data-id="element-860">
                              <span className={`text-xs px-2 py-0.5 rounded ${cliente.estado === 'visitado' ? 'bg-green-900 text-green-200' : cliente.estado === 'sin_compra' ? 'bg-yellow-900 text-yellow-200' : 'bg-gray-900 text-gray-200'}`} data-id="element-861">
                                {cliente.estado}
                              </span>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>)}
      </div>
    </div>;
}